import { DataSource } from '@angular/cdk/collections';
import { FormArray, FormControl } from '@angular/forms';
import { MatLegacyPaginator } from '@angular/material/legacy-paginator';
import { Dictionary } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import {
    BehaviorSubject,
    bufferTime,
    combineLatest,
    merge,
    Observable,
    of,
    Subject,
    Subscription,
    switchMap,
} from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    first,
    map, pairwise,
    shareReplay,
    startWith,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { AccountsReceivableLedgerEntity, AccountsReceivableLedgerTypeEnum, financialRound, isBookingPositionTypeEnum } from '../../../../dave-data-module/entities/accounts-receivable-ledger.entity';
import { LedgerImportARLCalculationPreChargeByType, LedgerImportARLSurchargesByType } from '../../../../dave-data-module/entities/ledger-import.entity';
import { State } from '../../../../dave-data-module/State';
import { getCustomerDictionary } from '../../../../dave-data-module/State/selectors/customers.selectors';
import { getValueChangesObservableFromFormGroup, SearchQueriesDebounceTime, stringSearch } from '../../../../helper/helper';
import { getAmountFromForm$ } from '../arl-amount-calculation-view/arl-table.helper';
import { calcPositionStrings, formatPositionString } from '../arl-calculation.helper';
import { arlBaseCostAllowanceType, ArlFormDataService, arlGapFormGroup, arlTableDataArlForm } from '../arl-form-data.service';
import { ArlTableService } from '../arl-table.service';
import {
    depthFromPosition, getExternalServiceEkWithPreCharge,
    getExternalServiceVK,
    getExternalServiceWithPreChargeAndCharge,
    getOtherEkWithPreCharge, getOtherVK, getOtherWithPreChargeAndCharge, getResourceEkWithPreCharge,
    getResourceVK,
    getResourceWithPreChargeAndCharge, getSalaryEk, getSalaryEkWithPreCharge,
    getSalaryVK,
    getSalaryWithPreChargeAndCharge,
} from './arl-lv-calculation.helper';
import { ArlDataService } from '../../../../dave-data-module/services/arl-data.service';
import { getQuantityTypeDictionary } from '../../../../dave-data-module/State/selectors/accounting.selector';
import { LvFilterValues } from './arl-lv-calculation.component';
import { FilterTypes } from '../../../../services/default-filter.service';

function getEmptyTableData() {
    return {
        minutes: null,
        SoEk: null,
        SoEkRabatt: null,
        SoAntEk: null,
        GEK: null,
        FlEk: null,
        quantity: null,
        quantityFormula: null,
        quantityFormulaUsedVariables: null,
        quantityTypeId: null,
        PartieHEur: null,
        salaryEk$: of(null),
        salaryEkWithPreCharge$: of(null),
        salaryCharge$: of(null),
        salaryWithPreChargeAndCharge$: of(null),
        salaryVk$: of(null),
        otherEkWithPreCharge$: of(null),
        otherCharge$: of(null),
        otherWithPreChargeAndCharge$: of(null),
        resourceEkWithPreCharge$: of(null),
        resourceWithPreChargeAndCharge$: of(null),
        resourceVK$: of(null),
        externalServiceEkWithPreCharge$: of(null),
        externalServiceWithPreChargeAndCharge$: of(null),
        externalServiceVK$: of(null),
        notSalaryVk$: of(null),
        epVK$: of(null),
        posPreis$: of(null),
        materialId$: of(null),
        notSalaryVkWithOtherSurcharge$: of(null),
        chargeCount$: of(null),
        epVKWithOtherSurcharge$: of(null),
        posPreisWithNK$: of(null),
        supplierName: null,
        priceComparisonId$: of(null),
        hasGaps$: of(false) as Observable<boolean>,
        hasUnfilledGaps$: of(false) as Observable<boolean>,
        hasFilledGaps$: of(false) as Observable<boolean>,
    };
}

export interface ArlLvCalculationItem {
    id: number;
    minutes: FormControl<number>;
    SoEk: FormControl<number>;
    SoEkRabatt: FormControl<number>;
    SoAntEk: FormControl<number>;
    GEK: FormControl<number>;
    FlEk: FormControl<number>;
    PartieHEur: FormControl<number>;

    positionForm: FormControl<string>;
    position$: Observable<string>;
    bookingText: FormControl<string>;
    information: FormControl<string>;
    quantity: FormControl<number>;
    quantityFormula: FormControl<string>;
    quantityFormulaUsedVariables: FormControl<string[]>;
    quantityTypeId: FormControl<number>;

    salaryEk$: Observable<number>;
    salaryEkWithPreCharge$: Observable<number>;
    salaryCharge$: Observable<number>;
    salaryWithPreChargeAndCharge$: Observable<number>;
    salaryVk$: Observable<number>;

    otherEkWithPreCharge$: Observable<number>;
    otherCharge$: Observable<number>;
    otherWithPreChargeAndCharge$: Observable<number>;
    // otherVk$: Observable<number>;

    resourceEkWithPreCharge$: Observable<number>;
    // resourceCharge$: Observable<number>;
    resourceWithPreChargeAndCharge$: Observable<number>;
    resourceVK$: Observable<number>;

    externalServiceEkWithPreCharge$: Observable<number>;
    // externalServiceCharge$: Observable<number>;
    externalServiceWithPreChargeAndCharge$: Observable<number>;
    externalServiceVK$: Observable<number>;

    notSalaryVk$: Observable<number>;
    epVK$: Observable<number>;
    posPreis$: Observable<number>;

    notSalaryVkWithOtherSurcharge$: Observable<number>;
    epVKWithOtherSurcharge$: Observable<number>;
    posPreisWithNK$: Observable<number>;

    chargeCount$: Observable<number>;
    allowanceCount$: Observable<number>;
    materialId$: Observable<number>;

    isBookingPosition: boolean;
    isGroup: boolean;
    isGroupEnd: boolean;

    isOptional$: Observable<boolean>;

    depth$: Observable<number>;
    /**
     * for search
     */
    supplierName: string;
    hasGaps$: Observable<boolean>;
    hasUnfilledGaps$: Observable<boolean>;
    hasFilledGaps$: Observable<boolean>;
    groupEnd?: {
        label$: Observable<string>;
        amount$: Observable<number>;
        costAmountVM$: Observable<{
            costAmount: number;
            zs: number;
        }>;
        amountWithoutParentAllowanceCharges$: Observable<number>;
        surchargeAllowances$: Observable<
            {
                title: string;
                percent: number;
                arlType: AccountsReceivableLedgerTypeEnum;
                type: arlBaseCostAllowanceType;
            }[]
        >;
    };
    priceComparisonId$: Observable<number>;
}

function createGapObservables(gapsControl: FormArray<arlGapFormGroup>) {
    const hasGaps$ = gapsControl.valueChanges.pipe(
        startWith(null),
        map(() => gapsControl.length > 0),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
    );
    const hasUnfilledGaps$ = gapsControl.valueChanges.pipe(
        startWith(null),
        map(() => gapsControl.controls.some((gap) => !gap.controls.body.value?.trim())),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
    );
    const hasFilledGaps$ = gapsControl.valueChanges.pipe(
        startWith(null),
        map(() => gapsControl.controls.some((gap) => !!gap.controls.body.value?.trim())),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
    );
    return { hasGaps$, hasUnfilledGaps$, hasFilledGaps$ };
}

/**
 * Data source for the ArlLvCalculation view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
export class ArlLvCalculationDataSource extends DataSource<ArlLvCalculationItem> {
    paginator: MatLegacyPaginator | undefined;
    searchQuery$: Observable<string | null>;
    lvFilterValues$: Observable<LvFilterValues> | undefined;
    arlIds$: Observable<number[]> | undefined;
    globalPreCharge$: Observable<number> | undefined;
    arlCalculationPreChargeByTypes$: Observable<LedgerImportARLCalculationPreChargeByType[]> | undefined;
    arlCalculationPreChargeByTypesMap$: Observable<Map<AccountsReceivableLedgerTypeEnum, number>> | undefined;
    arlGlobalSurchargesByTypes$: Observable<LedgerImportARLSurchargesByType[]> | undefined;
    data$: Observable<ArlLvCalculationItem[]> | undefined;
    filteredDataLength$ = new BehaviorSubject<number>(0);
    surchargeForOther$: Observable<number> | undefined;
    // partieHEur$: Observable<number> | undefined;
    posPriceSum$: Observable<number> | undefined;
    ARLForm: FormArray<arlTableDataArlForm>;
    itemNumbersLocked$ = new BehaviorSubject<boolean>(false);
    private itemNumberRecalculated$ = new Subject<void>();
    public notifyItemNumberRecalculated(): void {
        this.itemNumberRecalculated$.next();
    }

    private formSubscription: Subscription | undefined;
    private formSubscription1: Subscription | undefined;
    private formSubscription2: Subscription | undefined;
    private formSubscription3: Subscription | undefined;
    private arlsWithMissingPosNumbers: boolean = false;

    constructor(private arlFormDataService: ArlFormDataService, private store: Store<State>, private arlTableService: ArlTableService, protected arlDataService: ArlDataService) {
        super();
    }
    public initData() {
        if (this.arlIds$ && this.arlCalculationPreChargeByTypes$ && this.arlGlobalSurchargesByTypes$ && this.globalPreCharge$ && this.surchargeForOther$) {
            this.arlCalculationPreChargeByTypesMap$ = this.arlCalculationPreChargeByTypes$.pipe(
                map((arlCalculationPreChargeByTypes) => new Map(arlCalculationPreChargeByTypes.map(({ arlType, percentage }) => [arlType, percentage]))),
                shareReplay({ bufferSize: 1, refCount: true }),
            );

            const salaryCharge$ = this.arlGlobalSurchargesByTypes$.pipe(
                map((charges) => charges.find((charge) => charge.arlType === AccountsReceivableLedgerTypeEnum.EmployeeArl)?.percentage || 0),
                distinctUntilChanged(),
                shareReplay({ bufferSize: 1, refCount: true }),
            );
            const otherCharge$ = this.arlGlobalSurchargesByTypes$.pipe(
                map((charges) => charges.find((charge) => charge.arlType === AccountsReceivableLedgerTypeEnum.MaterialArl)?.percentage || 0),
                distinctUntilChanged(),
                shareReplay({ bufferSize: 1, refCount: true }),
            );
            const resourceCharge$ = this.arlGlobalSurchargesByTypes$.pipe(
                map((charges) => charges.find((charge) => charge.arlType === AccountsReceivableLedgerTypeEnum.ResourceArl)?.percentage || 0),
                distinctUntilChanged(),
                shareReplay({ bufferSize: 1, refCount: true }),
            );
            const externalServiceCharge$ = this.arlGlobalSurchargesByTypes$.pipe(
                map((charges) => charges.find((charge) => charge.arlType === AccountsReceivableLedgerTypeEnum.ExternalService)?.percentage || 0),
                distinctUntilChanged(),
                shareReplay({ bufferSize: 1, refCount: true }),
            );

            this.data$ = combineLatest([this.arlIds$ /*.pipe(tap((arlIds$) => console.log('arlIds$: ' + arlIds$)))*/, this.arlDataService.selectAllArlsDict()]).pipe(
                switchMap(([arlIds, arlDict]) => {
                    // console.log('arlIds', arlIds);
                    // alert('getFlatChildrenArrayWithGroupEnd$');
                    return arlIds.length ? getFlatChildrenArrayWithGroupEnd$(arlIds, this.arlFormDataService, arlDict) : of([] as { form: arlTableDataArlForm; isGroupEnd: boolean }[]);
                }),
                distinctUntilChanged(
                    (a, b) =>
                        a.length === b.length &&
                        a.every((aa, aaIndex) => {
                            const bb = b[aaIndex];
                            return aa.form === bb.form && aa.isGroupEnd === bb.isGroupEnd;
                        }),
                ),
                withLatestFrom(this.store.select(getCustomerDictionary), this.store.select(getQuantityTypeDictionary)),
                map(([arlForms, customerDict, qTDict]) => {
                    // console.log('arlForms', arlForms);
                    return (
                        arlForms
                            // .map(({form, isGroupEnd}, i, arr) => {
                            //     const parentId = form.getRawValue().ParentId;
                            //     const next = arr[i + 1];
                            //     const lastChild = parentId && (!next || (next.getRawValue().ParentId !== parentId && next.getRawValue().ParentId !== form.getRawValue().Id)); // todo so bauen wie in der anderen tabelle, nested groups tuen so nicht
                            //     if (lastChild) {
                            //         const parentIndex = arr.findIndex((f) => f.getRawValue().Id === parentId);
                            //
                            //         return [
                            //             { form, isGroupEnd: null },
                            //             {
                            //                 form: null,
                            //                 groupEnd: {
                            //                     parentId,
                            //                     arlsBetween: arr.slice(parentIndex + 1, i + 1),
                            //                 },
                            //             },
                            //         ];
                            //     } else {
                            //         return [{ form, isGroupEnd: null }];
                            //     }
                            // })
                            // .flat()
                            .map<ArlLvCalculationItem>(({ form, isGroupEnd }, i, arr) => {
                                const position$ = merge(
                                    getValueChangesObservableFromFormGroup(form, 'ItemNumber'),
                                    this.itemNumberRecalculated$.pipe(map(() => form.controls.ItemNumber.getRawValue())),
                                ).pipe(
                                    distinctUntilChanged(),
                                    shareReplay({ bufferSize: 1, refCount: true }),
                                );
                                if (form.controls.ItemNumber.value === ' ') {
                                    this.arlsWithMissingPosNumbers = true;
                                }
                                const depth$ = position$.pipe(map(depthFromPosition));
                                const isOptional$ = getValueChangesObservableFromFormGroup(form, 'IsOptional')

                                const allowanceCount$ = form.controls.BaseCostAllowances.valueChanges.pipe(
                                    startWith(null),
                                    map(() => form.controls.BaseCostAllowances.value.filter((v) => !v.arlType && v.percent < 0).length),
                                );

                                const chargeCount$ = form.controls.BaseCostAllowances.valueChanges.pipe(
                                    startWith(null),
                                    map(() => form.controls.BaseCostAllowances.value.filter((v) => !v.arlType && v.percent > 0).length),
                                );
                                if (isGroupEnd) {
                                    const parentIndex = arr.findIndex((f) => f.form.controls.Id.getRawValue() === form.controls.Id.getRawValue());
                                    if (parentIndex < 0 || parentIndex === i) {
                                        throw 'groupStartNotFound';
                                    }

                                    const arlsBetween = arr
                                        .slice(parentIndex + 1, i + 1)
                                        .filter((a) => !a.isGroupEnd)
                                        .map(({ form }) => form);
                                    // console.log(isGroupEnd);

                                    const amount$ = arlsBetween.length
                                        ? combineLatest(arlsBetween.map(a =>
                                            combineLatest([
                                                getValueChangesObservableFromFormGroup(a, 'IsOptional'),
                                                getAmountFromForm$(a, this.arlFormDataService).pipe(map(({ amount }) => amount))
                                            ]).pipe(
                                                map(([isOptional, amount]) => isOptional ? 0 : amount)
                                            )
                                        )).pipe(
                                            map((amounts) => amounts.reduce((p, c) => financialRound((p || 0) + (c || 0)), 0)),
                                            shareReplay({refCount: true, bufferSize: 1})
                                        )
                                        : of(0);
                                    const ret: ArlLvCalculationItem = {
                                        ...getEmptyTableData(),
                                        groupEnd: {
                                            label$: form.controls.Information.valueChanges.pipe(
                                                startWith(null),
                                                map(() => form.controls.Information.getRawValue()),
                                            ),
                                            // amount$: getAmountFromForm$(form, this.arlFormDataService).pipe(map(({ amount }) => amount)),
                                            amount$,
                                            costAmountVM$: arlsBetween.length
                                                ? combineLatest([combineLatest(arlsBetween.map(a =>
                                                    combineLatest([
                                                        getValueChangesObservableFromFormGroup(a, 'IsOptional'),
                                                        getAmountFromForm$(a, this.arlFormDataService).pipe(map(({ costAmount }) => costAmount))
                                                    ]).pipe(
                                                        map(([isOptional, amount]) => isOptional ? 0 : amount)
                                                    ),
                                                )),
                                                    amount$]).pipe(
                                                    map(([costAmounts, amount]) => {
                                                        const costAmount = costAmounts.reduce((p, c) => financialRound((p || 0) + (c || 0)), 0);
                                                        if (!costAmount) {
                                                            return null;
                                                        }
                                                        return {
                                                            costAmount,
                                                            zs: costAmount ? (amount / costAmount) - 1 : null
                                                        }
                                                    },
                                                ))
                                                : of(null),
                                            amountWithoutParentAllowanceCharges$: arlsBetween.length
                                                ? combineLatest(arlsBetween.map((a) => getAmountFromForm$(a, this.arlFormDataService, { ignoreParentAllowanceCharges: true }).pipe(map(({ amount }) => amount)))).pipe(
                                                      map((amounts) => amounts.reduce((p, c) => financialRound((p || 0) + (c || 0)), 0)),
                                                  )
                                                : of(0),
                                            surchargeAllowances$: form.controls.BaseCostAllowances.valueChanges.pipe(
                                                startWith(null),
                                                map(() => form.controls.BaseCostAllowances.getRawValue()?.filter(s => !s.arlType)),
                                            ),
                                        },
                                        isBookingPosition: false,
                                        isGroupEnd: true,
                                        isGroup: true,
                                        id: form.controls.Id.getRawValue(),
                                        positionForm: form.controls.ItemNumber,
                                        position$,
                                        bookingText: null,
                                        information: null,
                                        chargeCount$,
                                        allowanceCount$,
                                        depth$,
                                        isOptional$,
                                        ...createGapObservables(form.controls.Gaps),
                                    };
                                    return ret;
                                }

                                if (!isBookingPositionTypeEnum(form.controls.Type.value)) {
                                    const ret: ArlLvCalculationItem = {
                                        ...getEmptyTableData(),
                                        isBookingPosition: false,
                                        isGroupEnd: false,
                                        isGroup: form.controls.Type.getRawValue() === AccountsReceivableLedgerTypeEnum.Group,
                                        id: form.controls.Id.getRawValue(),
                                        positionForm: form.controls.ItemNumber, //todo checken ob das tut und ggf asynchron machen
                                        position$,
                                        bookingText: form.controls.BookingText,
                                        information: form.controls.Information,

                                        chargeCount$,
                                        allowanceCount$,
                                        depth$,
                                        isOptional$,
                                        ...createGapObservables(form.controls.Gaps),
                                    };
                                    return ret;
                                }
                                function getValueChangesObservable(control: keyof typeof form.controls.lvCalculation.controls) {
                                    return getValueChangesObservableFromFormGroup(form.controls.lvCalculation, control);
                                }
                                // const salaryEk$ = combineLatest([getValueChangesObservable('PartieHEur'), getValueChangesObservable('minutes')]).pipe(
                                //     map(([partieHEur, minutes]) => {
                                //         return salaryEk(partieHEur, minutes);
                                //     }),
                                //     shareReplay({ bufferSize: 1, refCount: true }),
                                // );


                                const preventGlobalPreCharge$ = form.controls.QuantityTypeId.valueChanges.pipe(
                                    startWith(form.controls.QuantityTypeId?.value),
                                    map(value => !!value && qTDict[value]?.PreventGlobalPreCharge),
                                    distinctUntilChanged()
                                );


                                const salaryEk$: Observable<number> = merge(this.arlTableService.lvUpdate$, getValueChangesObservable('PartieHEur'), getValueChangesObservable('minutes')).pipe(
                                    startWith(null), // initial trigger
                                    map(() => {
                                        const partieHEur = form.controls.lvCalculation.controls.PartieHEur.getRawValue();
                                        const minutes = form.controls.lvCalculation.controls.minutes.getRawValue();
                                        return getSalaryEk(partieHEur, minutes);
                                    }),
                                    shareReplay({ bufferSize: 1, refCount: true }),
                                );
                                const salaryEkWithPreCharge$ = combineLatest([salaryEk$, this.arlCalculationPreChargeByTypesMap$]).pipe(
                                    map(([salaryEk, arlCalculationPreChargeByTypes]) => {
                                        const preChargePercentage = arlCalculationPreChargeByTypes.get(AccountsReceivableLedgerTypeEnum.EmployeeArl);
                                        return getSalaryEkWithPreCharge(salaryEk, preChargePercentage);
                                    }),
                                    // tap(() => console.log('salaryEkWithPreCharge$')),
                                    shareReplay({ bufferSize: 1, refCount: true }),
                                );
                                const salaryWithPreChargeAndCharge$ = combineLatest([salaryEkWithPreCharge$, salaryCharge$]).pipe(
                                    map(([salaryEkWithPreCharge, salaryCharge]) => getSalaryWithPreChargeAndCharge(salaryEkWithPreCharge, salaryCharge)),
                                );

                                const salaryVk$ = combineLatest([salaryWithPreChargeAndCharge$, this.globalPreCharge$, preventGlobalPreCharge$]).pipe(
                                    map(([salary,  globalCharge, prevent]) => getSalaryVK(salary, globalCharge, prevent)),
                                    distinctUntilChanged(),
                                    shareReplay({ refCount: true, bufferSize: 1 })
                                );

                                const otherEkWithPreCharge$ = combineLatest([getValueChangesObservable('SoEk').pipe(debounceTime(5)), getValueChangesObservable('SoEkRabatt'), getValueChangesObservable('SoAntEk'), this.arlCalculationPreChargeByTypesMap$]).pipe(
                                    map(([SoEk, SoEkRabatt, SoAntEk, arlCalculationPreChargeByTypes]) => {
                                        const preChargePercentage = arlCalculationPreChargeByTypes.get(AccountsReceivableLedgerTypeEnum.MaterialArl);
                                        return getOtherEkWithPreCharge(SoEk, SoEkRabatt, SoAntEk, preChargePercentage);
                                        // ((SoEk || 0) * (1 - (SoEkRabatt || 0) / 100) + SoAntEk || 0) * (1 + (preChargePercentage || 0));

                                    }),
                                    shareReplay({ bufferSize: 1, refCount: true }),
                                );
                                const otherWithPreChargeAndCharge$ = combineLatest([otherEkWithPreCharge$, otherCharge$]).pipe(
                                    map(([otherEkWithPreCharge, otherCharge]) => getOtherWithPreChargeAndCharge(otherEkWithPreCharge, otherCharge))
                                );
                                const otherVk$ = combineLatest([
                                    otherWithPreChargeAndCharge$,
                                    this.globalPreCharge$,
                                    preventGlobalPreCharge$
                                ]).pipe(
                                    map(([other, globalCharge, prevent]) => getOtherVK(other, globalCharge, prevent)),
                                    distinctUntilChanged(),
                                    shareReplay({ refCount: true, bufferSize: 1 })
                                );

                                const resourceEkWithPreCharge$ = combineLatest([
                                    getValueChangesObservable('GEK'),
                                    this.arlCalculationPreChargeByTypesMap$,
                                    preventGlobalPreCharge$
                                ]).pipe(
                                    map(([gEK, preChargeMap, prevent]) => {
                                        return getResourceEkWithPreCharge(gEK, preChargeMap.get(AccountsReceivableLedgerTypeEnum.ResourceArl), prevent);
                                        // const preChargePercentage = prevent
                                        //     ? 0
                                        //     : preChargeMap.get(AccountsReceivableLedgerTypeEnum.ResourceArl);
                                        // return (gEK || 0) * (1 + (preChargePercentage || 0));
                                    }),
                                    shareReplay({ bufferSize: 1, refCount: true })
                                );

                                const resourceWithPreChargeAndCharge$ = combineLatest([resourceEkWithPreCharge$, resourceCharge$]).pipe(
                                    map(([resourceEkWithPreCharge, resourceCharge]) => getResourceWithPreChargeAndCharge(resourceEkWithPreCharge, resourceCharge)),
                                );

                                const resourceVK$ = combineLatest([
                                    resourceWithPreChargeAndCharge$,
                                    this.globalPreCharge$,
                                    preventGlobalPreCharge$
                                ]).pipe(
                                    map(([resource, globalCharge, prevent]) => getResourceVK(resource, globalCharge, prevent)),
                                    //     prevent
                                    //         ? resource
                                    //         : resource * (1 + (globalCharge || 0))
                                    // ),
                                    distinctUntilChanged(),
                                    shareReplay({ bufferSize: 1, refCount: true })
                                );


                                const externalServiceEkWithPreCharge$ = combineLatest([getValueChangesObservable('FlEk'), this.arlCalculationPreChargeByTypesMap$]).pipe(
                                    map(([FlEk, arlCalculationPreChargeByTypes]) => {
                                        const preChargePercentage = arlCalculationPreChargeByTypes.get(AccountsReceivableLedgerTypeEnum.ExternalService);
                                        return getExternalServiceEkWithPreCharge(FlEk, preChargePercentage);
                                        // return (FlEk || 0) * (1 + (preChargePercentage || 0));
                                    }),
                                    shareReplay({ bufferSize: 1, refCount: true }),
                                );
                                const externalServiceWithPreChargeAndCharge$ = combineLatest([externalServiceEkWithPreCharge$, externalServiceCharge$]).pipe(
                                    map(([externalServiceEkWithPreCharge, externalServiceCharge]) => getExternalServiceWithPreChargeAndCharge(externalServiceEkWithPreCharge, externalServiceCharge)),
                                );

                                const externalServiceVK$ = combineLatest([
                                    externalServiceWithPreChargeAndCharge$,
                                    this.globalPreCharge$,
                                    preventGlobalPreCharge$
                                ]).pipe(
                                    map(([externalService, globalCharge, prevent]) => getExternalServiceVK(externalService, globalCharge, prevent)),
                                    //     prevent
                                    //         ? externalService
                                    //         : externalService * (1 + (globalCharge || 0))
                                    // ),
                                    distinctUntilChanged(),
                                    shareReplay({ bufferSize: 1, refCount: true })
                                );


                                const notSalaryVk$ = combineLatest([otherVk$, resourceVK$, externalServiceVK$]).pipe(map((notSalaryVks) => notSalaryVks.reduce((a, b) => a + (b || 0), 0)),
                                    shareReplay({refCount: true, bufferSize: 1}));
                                const epVK$ = combineLatest([notSalaryVk$, salaryVk$]).pipe(map((vks) => vks.reduce((a, b) => a + (b || 0), 0)));
                                const quantity$ = form.controls.Quantity.valueChanges.pipe(
                                    startWith(null),
                                    map(() => form.controls.Quantity.value),
                                );

                                const posPreis$ = combineLatest([epVK$, quantity$]).pipe(
                                    map(([piecePrice, quantity]) => (piecePrice || 0) * (quantity || 0)),
                                    shareReplay({refCount: true, bufferSize: 1})
                                );

                                const notSalaryVkWithOtherSurcharge$ = combineLatest([this.posPriceSum$, this.surchargeForOther$, quantity$]).pipe(
                                    switchMap(([posPreisSum, surchargeForOther, quantity]) => {

                                        return combineLatest([notSalaryVk$, posPreis$]).pipe(first(), map(([notSalaryVk, posPreis]) => ({
                                                         posPreisSum, surchargeForOther, quantity,
                                            notSalaryVk,
                                            posPreis
                                        })))
                                    }),
                                    map(({posPreisSum, surchargeForOther, quantity, notSalaryVk, posPreis}/* notSalaryVk, posPreis*/) => {
                                        // console.log('notSalaryVkWithOtherSurcharge$');



                                        const posAnteil = posPreis / posPreisSum;
                                        const chargeForOther = quantity && !form.controls.IsOptional.getRawValue() ? (surchargeForOther * posAnteil) / quantity : 0;
                                        return notSalaryVk + chargeForOther;
                                    }),
                                );

                                let supplierId = form.controls.SupplierId.getRawValue();



                                return {
                                    isBookingPosition: true,
                                    isGroupEnd: false,
                                    isGroup: false,
                                    id: form.controls.Id.getRawValue(),
                                    ...form.controls.lvCalculation.controls,
                                    positionForm: form.controls.ItemNumber,
                                    position$,
                                    bookingText: form.controls.BookingText,
                                    information: form.controls.Information,
                                    quantity: form.controls.Quantity,
                                    quantityFormula: form.controls.QuantityFormula,
                                    quantityFormulaUsedVariables: form.controls.QuantityFormulaUsedVariables,
                                    quantityTypeId: form.controls.QuantityTypeId,

                                    salaryEk$,
                                    salaryEkWithPreCharge$,
                                    salaryCharge$,
                                    salaryWithPreChargeAndCharge$,
                                    salaryVk$,

                                    otherEkWithPreCharge$,
                                    otherCharge$,
                                    otherWithPreChargeAndCharge$,

                                    resourceEkWithPreCharge$,
                                    resourceWithPreChargeAndCharge$,
                                    resourceVK$,

                                    externalServiceEkWithPreCharge$,
                                    externalServiceWithPreChargeAndCharge$,
                                    externalServiceVK$,

                                    notSalaryVk$,
                                    epVK$,
                                    posPreis$,
                                    // posPreis$: getAmountFromForm$(form, this.arlFormDataService).pipe(map(({amount}) => amount)),
                                    notSalaryVkWithOtherSurcharge$,
                                    epVKWithOtherSurcharge$: combineLatest([notSalaryVkWithOtherSurcharge$ /*.pipe(tap(() => console.log('notSalaryVkWithOtherSurcharge$')))*/, salaryVk$ /*.pipe(tap(() => console.log('salaryVk$')))*/]).pipe(
                                        map((vks) => vks.reduce((a, b) => a + (b || 0), 0)),
                                    ),
                                    posPreisWithNK$: getValueChangesObservableFromFormGroup(form, 'IsOptional').pipe(
                                        switchMap((isOptional) => isOptional ? of(null) : getAmountFromForm$(form, this.arlFormDataService, { ignoreParentAllowanceCharges: false }).pipe(map(({ amount }) => amount)))
                                    ),

                                    chargeCount$,
                                    allowanceCount$,
                                    materialId$: getValueChangesObservableFromFormGroup(form, 'MaterialId'),

                                    depth$,
                                    isOptional$,

                                    supplierName: supplierId ? customerDict[supplierId]?.Name : null,
                                    priceComparisonId$: getValueChangesObservableFromFormGroup(form, 'PriceComparisonId'),

                                    ...createGapObservables(form.controls.Gaps),
                                };
                            })
                    );
                }),
                shareReplay({ bufferSize: 1, refCount: true }),
            );
            this.posPriceSum$ = this.data$.pipe(
                tap((data) => {
                    if (this.arlsWithMissingPosNumbers) {
                        recalculateItemNumbersFromLvTableItems(data, this.itemNumbersLocked$.value);
                        this.arlsWithMissingPosNumbers = false;
                    }
                }),
                switchMap((data) => {
                     const filteredData = data.filter(d => d.quantity);
                     if (filteredData.length === 0) {
                        return of([0])
                    }

                    return combineLatest(filteredData.map(d => combineLatest([
                        d.isOptional$,
                        d.posPreis$,
                        d.quantity.valueChanges.pipe(startWith(null), map(() => !!d.quantity.getRawValue()), distinctUntilChanged())
                        ]).pipe(
                            map(([isOptional, posPrice, hasQuantity]) => ({isOptional, posPrice, hasQuantity })),
                        )
                    )).pipe(
                        map((items) => {
                            return items.filter(d => !d.isOptional && !!d.hasQuantity).map(({posPrice}) => posPrice);
                        }),
                    );
                }),
                map((sums) => sums.reduce((a, b) => a + b, 0)),
                // debounceTime(1),
                shareReplay({ bufferSize: 1, refCount: true }),
               // tap((v) => console.log('posPriceSum$', v)),
            );
        } else {
            throw Error('Please set the arlIds$ and arlCalculationPreChargeByTypes$ and globalPreCharge$ and surchargeForOther$ and arlGlobalSurchargesByTypes$ on the data source before call initData.');
        }
    }
     initFormControlSubscriptions() {
        if (this.data$ && this.ARLForm) {
            // todo buffer, triggers huge incremental CC
            function determineIfIsEPChange(toBeDetermined: { ep: number, arlId: number } | { salaryVk: number, arlId: number } | { eks: number[], arlId: number }): toBeDetermined is { ep: number, arlId: number } {
                return (toBeDetermined as {ep: number}).ep !== undefined;
            }
            function determineIfIsSalaryVkChange(toBeDetermined: { ep: number, arlId: number } | { salaryVk: number, arlId: number } | { eks: number[], arlId: number }): toBeDetermined is { salaryVk: number, arlId: number } {
                return (toBeDetermined as {salaryVk: number}).salaryVk !== undefined;
            }
            function determineIfIsEKsChange(toBeDetermined: { ep: number, arlId: number } | { salaryVk: number, arlId: number } | { eks: number[], arlId: number }): toBeDetermined is { eks: number[], arlId: number } {
                return (toBeDetermined as {eks: number[]}).eks !== undefined;
            }

            this.formSubscription?.unsubscribe();
            this.formSubscription = this.data$
                .pipe(
                    switchMap((data) => {
                        const sub1 =  merge(...data.map((d) => d.epVKWithOtherSurcharge$.pipe(map((ep) => ({ ep, arlId: d.id })))))
                        const sub2 =  merge(...data.map((d) => d.salaryVk$.pipe(map((salaryVk) => ({ salaryVk, arlId: d.id })))));
                        const sub3 =  merge(...data.map((d) => combineLatest([d.salaryEkWithPreCharge$, d.otherEkWithPreCharge$, d.resourceEkWithPreCharge$, d.externalServiceEkWithPreCharge$]).pipe(map((eks) => ({ eks, arlId: d.id })))))
                        // return combineLatest([sub1, sub2, sub3])
                        return merge(sub1, sub2, sub3);
                    }),
                    bufferTime(50),
                    filter(b => b.length !== 0),
                )
                .subscribe((buffer: Array<{ ep: number, arlId: number } | { salaryVk: number, arlId: number } | { eks: number[], arlId: number }>) => {
                    console.log('formSubscription', buffer);

                    const newValuesForArl = new Map<number, {
                        BaseCostGross?: number,
                        BaseCost?: number,
                        SalaryBaseCost?: number,
                        CostAmount?: number,
                    }>
                    for (let updatedValue of buffer) {
                        const form = this.arlFormDataService.getForm(updatedValue.arlId);


                        if (isBookingPositionTypeEnum(form.controls.Type.getRawValue())) {
                            if (!newValuesForArl.has(updatedValue.arlId)) {
                                newValuesForArl.set(updatedValue.arlId, {});
                            }
                            if (determineIfIsEPChange(updatedValue)) {
                                const roundedEp = financialRound(updatedValue.ep || 0);

                                newValuesForArl.set(updatedValue.arlId, {
                                    ...newValuesForArl.get(updatedValue.arlId),
                                    BaseCost: roundedEp,
                                    BaseCostGross: financialRound(roundedEp * ((form.controls.Tax.value || 0) / 100 + 1))
                                });
                            } else if (determineIfIsSalaryVkChange(updatedValue)) {
                                const roundedSalaryVk = financialRound(updatedValue.salaryVk || 0);
                                newValuesForArl.set(updatedValue.arlId, {
                                    ...newValuesForArl.get(updatedValue.arlId),
                                    SalaryBaseCost: roundedSalaryVk
                                });

                            } else if (determineIfIsEKsChange(updatedValue)) {
                                const roundedEks = financialRound(updatedValue.eks.reduce((a, b) => a + b, 0));
                                newValuesForArl.set(updatedValue.arlId, {
                                    ...newValuesForArl.get(updatedValue.arlId),
                                    CostAmount: roundedEks
                                });
                            }
                        }

                    }
                    console.log('newValuesForArl', newValuesForArl)
                    const patchValuesForArl = new Map<number, {
                        BaseCostGross?: number,
                        BaseCost?: number,
                        SalaryBaseCost?: number,
                        CostAmount?: number,
                    }>
                    newValuesForArl.forEach((value, arlId) => {
                        const form = this.arlFormDataService.getForm(arlId);
                        const patchValues: Partial<Record<keyof Pick<arlTableDataArlForm['controls'], 'BaseCostGross' | 'BaseCost' | 'SalaryBaseCost' | 'CostAmount'>, number>> = {};
                        if (value.BaseCost !== undefined && value.BaseCostGross !== undefined) {
                            if (form?.controls.BaseCost && (form.controls.BaseCost.value || 0) !== value.BaseCost) {
                                patchValues.BaseCostGross = value.BaseCostGross;
                                patchValues.BaseCost = value.BaseCost;
                            }
                        }
                        if (value.SalaryBaseCost !== undefined) {
                            if (form?.controls.SalaryBaseCost && (form.controls.SalaryBaseCost.value || 0) !== value.SalaryBaseCost) {
                                patchValues.SalaryBaseCost = value.SalaryBaseCost;
                            }
                        }
                        if (value.CostAmount !== undefined) {
                            if (form?.controls.CostAmount && (form.controls.CostAmount.value || 0) !== value.CostAmount) {
                                patchValues.CostAmount = value.CostAmount;
                            }
                        }
                        if (Object.values(patchValues).length > 0) {
                            patchValuesForArl.set(arlId, patchValues);
                            //console.log('patchValues', patchValues);
                            form.patchValue(patchValues);
                            Object.keys(patchValues).forEach((key) => {
                                form.get(key).markAsDirty();
                            })
                        }
                    })

                    // todo löst endlos loop aus
                    // if (patchValuesForArl.size > 0) {
                        // const currentValue = this.ARLForm.getRawValue();
                        // function setPrice(arlTableDataArlForm: arlTableDataArlForm['value']) {
                        //     const match = patchValuesForArl.get(arlTableDataArlForm.Id);
                        //     if (match) {
                        //         Object.keys(match).forEach((key) => {
                        //             arlTableDataArlForm[key] = match[key];
                        //         })
                        //     }
                        //     arlTableDataArlForm.childArls?.forEach(arl => setPrice(arl))
                        // }
                        // currentValue.forEach((value) => {setPrice(value)})
                        // this.ARLForm.patchValue(currentValue)
                    // }
                })



            //
            // this.formSubscription1?.unsubscribe();
            // this.formSubscription1 = this.data$
            //     .pipe(
            //         switchMap((data) => {
            //             // console.log('data', data);
            //             return merge(...data.map((d) => d.epVKWithOtherSurcharge$.pipe(map((ep) => ({ ep, arlId: d.id })))));
            //         }),
            //     )
            //     .subscribe(({ ep, arlId }) => {
            //         // console.log('formSubscription');
            //         // console.log('ep', ep, 'arlId', arlId);
            //         const roundedEp = financialRound(ep || 0);
            //         const form = this.arlFormDataService.getForm(arlId);
            //         if (form?.controls.BaseCost && (form?.controls.BaseCost.value || 0) !== roundedEp) {
            //             form.controls.BaseCost.setValue(roundedEp);
            //             form.controls.BaseCostGross.setValue(financialRound(form.controls.BaseCost.value * (form.controls.Tax.value / 100 + 1)));
            //             form.controls.BaseCostGross.markAsDirty();
            //             form.controls.BaseCost.markAsDirty();
            //         }
            //     });
            // this.formSubscription2?.unsubscribe();
            // this.formSubscription2 = this.data$
            //     .pipe(
            //         switchMap((data) => {
            //             // console.log('data', data);
            //             return merge(...data.map((d) => d.salaryVk$.pipe(map((salaryVk) => ({ salaryVk, arlId: d.id })))));
            //         }),
            //     )
            //     .subscribe(({ salaryVk, arlId }) => {
            //         // console.log('salaryVk', salaryVk, 'arlId', arlId);
            //         // console.log('formSubscription2');
            //         const roundedSalaryVk = financialRound(salaryVk || 0);
            //         const form = this.arlFormDataService.getForm(arlId)?.controls.SalaryBaseCost;
            //         if (form && (form.value || 0) !== roundedSalaryVk) {
            //             form.setValue(roundedSalaryVk);
            //             form.markAsDirty();
            //         }
            //     });
            // this.formSubscription3?.unsubscribe();
            // this.formSubscription3 = this.data$
            //     .pipe(
            //         switchMap((data) => {
            //             // console.log('data', data);
            //             return merge(...data.map((d) => combineLatest([d.salaryEkWithPreCharge$, d.otherEkWithPreCharge$, d.resourceEkWithPreCharge$, d.externalServiceEkWithPreCharge$]).pipe(map((eks) => ({ eks, arlId: d.id })))));
            //         }),
            //     )
            //     .subscribe(({ eks, arlId }) => {
            //         // console.log('formSubscription3');
            //         const roundedEks = financialRound(eks.reduce((a, b) => a + b, 0));
            //         const form = this.arlFormDataService.getForm(arlId)?.controls.CostAmount;
            //         if (form && (form.value || 0) !== roundedEks) {
            //             form.setValue(roundedEks);
            //         }
            //     });
        } else {
            throw Error('Please call initData before call initFormControlSubscriptions.');
        }
    }
    public renderedData: ArlLvCalculationItem[] = [];
    /**
     * Connect this data source to the table. The table will only update when
     * the returned stream emits new items.
     * @returns A stream of the items to be rendered.
     */
    connect(): Observable<ArlLvCalculationItem[]> {
        let pageSizeCache: number;
        let pageIndexCache: number;
        if (this.paginator && this.data$ && this.searchQuery$) {
            this.initFormControlSubscriptions();
            // Combine everything that affects the rendered data into one update
            // stream for the data-table to consume.

            return combineLatest([this.data$, merge(of(''), this.searchQuery$.pipe(debounceTime(SearchQueriesDebounceTime))).pipe(
                distinctUntilChanged((a, b) => (a || '').trim() === (b || '').trim()),
                startWith(''),
                pairwise(),
                ), this.lvFilterValues$ ?? of({
                [FilterTypes.Gaps]: false,
                [FilterTypes.UnfilledGaps]: false,
                [FilterTypes.FilledGaps]: false,
                [FilterTypes.WithoutPrices]: false,
            })]).pipe(
                // return this.data$.pipe(
                switchMap(([data, [prevSearchQuery, searchQuery], filterValues]) => {
                    const nonGapFiltered = getFilteredData(data, searchQuery);
                    if (searchQuery && !prevSearchQuery) {
                        pageIndexCache = this.paginator.pageIndex;
                        pageSizeCache = this.paginator.pageSize;
                        this.paginator.firstPage();
                    } else if (!searchQuery && prevSearchQuery) {
                        this.paginator.pageSize = pageSizeCache;
                        this.paginator.pageIndex = pageIndexCache;
                    }
                    const hasFilter = filterValues[FilterTypes.Gaps] || filterValues[FilterTypes.UnfilledGaps] || filterValues[FilterTypes.FilledGaps] || filterValues[FilterTypes.WithoutPrices];
                    const filteredData$: Observable<ArlLvCalculationItem[]> = hasFilter && nonGapFiltered.length > 0
                        ? combineLatest(nonGapFiltered.map((item) =>
                            combineLatest([
                                item.hasGaps$,
                                item.hasUnfilledGaps$,
                                item.hasFilledGaps$
                            ]).pipe(
                                withLatestFrom(item.posPreis$),
                                map(([[hasGaps, hasUnfilled, hasFilled], posPrice]) => ({
                                    item,
                                    hasGaps,
                                    hasUnfilled,
                                    hasFilled,
                                    posPrice,
                                })),
                            )
                          )).pipe(
                            map((items) =>
                                items
                                    .filter(({ hasGaps, hasUnfilled, hasFilled, posPrice }) => {
                                        if (filterValues[FilterTypes.WithoutPrices] &&  posPrice !== 0) return false;
                                        if (filterValues[FilterTypes.Gaps] && !hasGaps) return false;
                                        if (filterValues[FilterTypes.UnfilledGaps] && !hasUnfilled) return false;
                                        if (filterValues[FilterTypes.FilledGaps] && !hasFilled) return false;
                                        return true;
                                    })
                                    .map(({ item }) => item),
                            ),
                          )
                        : of(nonGapFiltered);
                    return filteredData$.pipe(
                        switchMap((filteredData) => {
                            this.filteredDataLength$.next(filteredData.length);
                            return this.paginator.page.pipe(
                                startWith(null),
                                map(() => this.getPagedData([...filteredData])),
                                tap((pagedData) => {
                                    this.renderedData = pagedData;
                                }),
                            );
                        }),
                    );
                }),
                // tap((data) => console.log('connected table data', data)),
            );
        } else {
            throw Error('Please set the searchQuery$ and call initData on the data source before connecting.');
        }
    }

    /**
     *  Called when the table is being destroyed. Use this function, to clean up
     * any open connections or free any held resources that were set up during connect.
     */
    disconnect(): void {
this.removeFormSubscriptions();
        }
    removeFormSubscriptions() {
        this.formSubscription?.unsubscribe();
        this.formSubscription1?.unsubscribe();
        this.formSubscription2?.unsubscribe();
        this.formSubscription3?.unsubscribe();

    }

    /**
     * Paginate the data (client-side). If you're using server-side pagination,
     * this would be replaced by requesting the appropriate data from the server.
     */
    private getPagedData(data: ArlLvCalculationItem[]): ArlLvCalculationItem[] {
        if (this.paginator) {
            const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
            return data.splice(startIndex, this.paginator.pageSize);
        } else {
            return data;
        }
    }
}
function getFilteredData(data: ArlLvCalculationItem[], searchQuery: string | null): ArlLvCalculationItem[] {
    if (searchQuery) {
        data = data.filter((item) => item.positionForm?.value?.toLowerCase().startsWith(searchQuery.toLowerCase()) || [item.bookingText?.value, item.information?.value, item.supplierName].some((v) => stringSearch(v, searchQuery)));
    }
    return data;
}

export function getFlatChildrenArrayWithGroupEnd$(arlIds: number[], arlFormDataService: ArlFormDataService, arlDict: Dictionary<AccountsReceivableLedgerEntity>) {
    return combineLatest(
        arlIds.map((id) => {
            let form = arlFormDataService.getForm(id);
            if (!form) {
                arlFormDataService.setFormByEntity(arlDict[id], arlDict);
                form = arlFormDataService.getForm(id);
            }
            return merge(
                form.controls.Type.valueChanges,
                form.controls.childArls.valueChanges.pipe(
                    debounceTime(10),
                    // tap(() => {
                    //     console.log('form.controls.childArls.valueChanges');
                    // }),
                ),
                form.controls.ARLIds.valueChanges,
            ).pipe(
                // tap((v) => console.log('merge triggered', v)),
                startWith(null),
                map(() => {
                    // alert('getChildrenArrayWithGroupEnd')
                    return getChildrenArrayWithGroupEnd(form);
                }),
                distinctUntilChanged((a, b) => a.length === b.length && a.every((c, i) => c.form === b[i].form && c.isGroupEnd === b[i].isGroupEnd)),
                // tap(() => {
                //     console.log('something changed');
                // }),
            );
        }),
    ).pipe(
        // tap(() => {
        //     console.log('something changed 2');
        // }),
        map((rows) => rows.flat()),
    );
}
function getChildrenArrayWithGroupEnd(arlForm: arlTableDataArlForm): { form: arlTableDataArlForm; isGroupEnd: boolean }[] {
    if (arlForm.controls.Type.getRawValue() === AccountsReceivableLedgerTypeEnum.Group) {
        const ret = [{ form: arlForm, isGroupEnd: false }, ...arlForm.controls.childArls.controls.map((i) => getChildrenArrayWithGroupEnd(i)).flat(), { form: arlForm, isGroupEnd: true }];
        // console.log(ret); todo
        return ret;
    }
    return [{ form: arlForm, isGroupEnd: false }];
}
export function recalculateItemNumbersFromLvTableItems(data: ArlLvCalculationItem[], itemNumbersFrozen: boolean, changeFromSort = false, notifyRecalculated?: () => void,) {
   //  console.log('recalculateItemNumbersFromLvTableItems', data, itemNumbersFrozen);
    function determineIfIsArlOrGroupEnd(toBeDetermined: ArlLvCalculationItem): toBeDetermined is ArlLvCalculationItem {
        return !toBeDetermined.isGroupEnd;
    }
    const positions = calcPositionStrings<ArlLvCalculationItem, ArlLvCalculationItem>(
        data,
        determineIfIsArlOrGroupEnd,
        (v) => v.isGroupEnd,
        (v) => v.isGroup && !v.isGroupEnd,
        (v) => v.positionForm.getRawValue(),
        !itemNumbersFrozen,
    );
    data.forEach((d, i) => {
        const formatedPosition = itemNumbersFrozen ? positions[i] : formatPositionString(positions[i]);
        if (!d.isGroupEnd && d.positionForm.getRawValue() !== formatedPosition) {
            d.positionForm.setValue(formatedPosition, { emitEvent: false });
            d.positionForm.markAsDirty();
        }
    });
    if (changeFromSort) {
        notifyRecalculated?.();
    }
}


